/**
 * Forgiving text search for lists filtered in the browser.
 *
 * People type the way they say things: "Lake view" for "Lakeview", "st johns"
 * for "St. John's", "Lakeveiw" with a slip of the finger. A plain
 * ``includes`` misses all three. This matcher:
 *
 * - ignores case, accents, spaces and punctuation ("Lake view" = "Lakeview");
 * - matches every word of the query on its own, in any order;
 * - forgives a small typo in a longer word (one slip from 4 letters, two
 *   from 8), so "Lakeveiw" still finds "Lakeview".
 *
 * Exact matches rank above typo matches, so the closest results stay on top.
 */

/** Lowercase, accents removed, anything but letters and digits dropped. */
function compact(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '')
}

function words(text: string): string[] {
  return text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
}

/** Typos forgiven in a query word of this length. */
function allowedSlips(length: number): number {
  if (length >= 8) return 2
  if (length >= 4) return 1
  return 0
}

/**
 * Smallest edit distance between ``needle`` and any stretch of ``haystack``
 * (approximate substring match), stopping early once above ``limit``.
 */
function closestDistance(needle: string, haystack: string, limit: number): number {
  let previous = new Array<number>(haystack.length + 1).fill(0)
  for (let i = 1; i <= needle.length; i++) {
    const current = new Array<number>(haystack.length + 1)
    current[0] = i
    let rowBest = i
    for (let j = 1; j <= haystack.length; j++) {
      const cost = needle[i - 1] === haystack[j - 1] ? 0 : 1
      current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost)
      // A swapped pair ("ei" for "ie") counts as one slip.
      if (
        i > 1 &&
        j > 1 &&
        needle[i - 1] === haystack[j - 2] &&
        needle[i - 2] === haystack[j - 1]
      ) {
        current[j] = Math.min(current[j], (previous[j - 2] ?? i) + cost)
      }
      rowBest = Math.min(rowBest, current[j])
    }
    if (rowBest > limit) return limit + 1
    previous = current
  }
  return Math.min(...previous)
}

/**
 * How well ``text`` matches ``query``: 0 = no match, 2 = exact, 1 = only
 * with a forgiven typo. An empty query matches everything.
 */
export function searchScore(text: string, query: string): number {
  const queryWords = words(query)
  if (!queryWords.length) return 2
  const target = compact(text)
  if (!target) return 0
  // The whole query, spaces dropped: "Lake view" -> "lakeview".
  if (target.includes(queryWords.join(''))) return 2

  let score = 2
  for (const word of queryWords) {
    if (target.includes(word)) continue
    const slips = allowedSlips(word.length)
    if (!slips || closestDistance(word, target, slips) > slips) return 0
    score = 1
  }
  return score
}

export function matchesSearch(text: string, query: string): boolean {
  return searchScore(text, query) > 0
}

/**
 * The items matching ``query``, exact matches first, each group in its
 * original order. ``textOf`` gives the searchable text of an item.
 */
export function filterBySearch<T>(items: T[], query: string, textOf: (item: T) => string): T[] {
  if (!words(query).length) return items
  const exact: T[] = []
  const close: T[] = []
  for (const item of items) {
    const score = searchScore(textOf(item), query)
    if (score === 2) exact.push(item)
    else if (score === 1) close.push(item)
  }
  return [...exact, ...close]
}
